import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {DnsZone} from '../types';
import {DelegationBadge} from '../components/DelegationBadge';
import {NameserverCount} from '../components/NameserverCount';
import {
    providerColor,
    providerLabel,
} from '../providerLabels';
import config from '../config';

// ⚠️ PROVIDER IS A COLUMN, NOT A GROUPING.
//
// A portfolio spread across three providers is ONE list with provenance as an
// attribute — not three tables, not a grouped table, not a provider switcher.
// That is the point of the neutral layer: `provider` is the only field in the
// schema that reveals where a zone came from, and it earns a column rather than
// restructuring the page.
export const useZonesTableColumns = () => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<DnsZone>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // FNP-1284 - NO WIDTH ON THE FLEXIBLE COLUMN. Leave it that way.
            //
            // A column with no width takes the space the fixed columns leave. Giving
            // this one a grid fraction (`2fr`) instead made it content-size: the
            // fixed columns are committed first, and the name column then grew to fit
            // its longest value, pushing the table wider than its container and
            // raising a horizontal scrollbar.
            //
            // `minWidth` in px is fine here if a floor is ever wanted. A grid
            // fraction is not: mantine-datatable puts the value in an inline style on
            // a real <table> cell, where `fr` is not a valid unit.
            render: (item: DnsZone) => (
                // ⚠️ The link carries `connectionId`, because `/zones/:zoneId`
                // alone cannot address a zone — getDnsZone needs both. See
                // hooks/useZone for the full resolution order.
                <NavLink
                    to={`${config.basePath}/${encodeURIComponent(item.id)}?connectionId=${encodeURIComponent(item.connectionId)}`}
                >
                    <Text size={'sm'}>{item.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'provider',
            sortable: true,
            title: I18n.get('Provider'),
            render: (item: DnsZone) => (
                <Badge variant={'light'} color={providerColor(item.provider)}>
                    {providerLabel(item.provider)}
                </Badge>
            ),
        },
        {
            accessor: 'appearsDelegated',
            sortable: true,
            title: I18n.get('Delegation'),
            render: (item: DnsZone) => <DelegationBadge zone={item}/>,
        },
        {
            accessor: 'isPrivate',
            sortable: true,
            title: I18n.get('Visibility'),
            // `isPrivate` is `Boolean!` — non-null, so unlike the hosted-zones
            // equivalent there is no third state to render here.
            render: (item: DnsZone) => (
                <Badge
                    variant={'light'}
                    color={item.isPrivate ? 'orange' : 'blue'}
                >
                    {item.isPrivate ? I18n.get('Private') : I18n.get('Public')}
                </Badge>
            ),
        },
        {
            accessor: 'nameservers',
            sortable: true,
            title: I18n.get('Nameservers'),
            // The COUNT, not the list: four hostnames per row would drown the
            // table. The full delegation set is on the detail page.
            //
            // ⚠️ AND AN EMPTY LIST RENDERS AS UNKNOWN, NOT `0` (FNP-1289) — the
            // list path never reads the delegation set, so zero here is an
            // absence of information, not an answer. See NameserverCount.
            render: (item: DnsZone) => <NameserverCount zone={item}/>,
        },
        // ⚠️ THERE IS DELIBERATELY NO ZONE ID COLUMN.
        //
        // `DnsZone.id` is OPAQUE — Flexi's own identifier, minted by the
        // adapter, never the provider's. Putting it in front of an operator
        // invites them to paste it into the AWS console or a Cloudflare
        // dashboard, where it means nothing. The hosted-zones module shows its
        // zone id precisely because that one IS the Route 53 id.
    ];

    return {
        columns,
    };
};
